import axiosClient from './axiosClient';
import {
  ILocationGeofenceItem,
  IMapListResponse,
  IMapLocationQueryParams,
  IMapPersonQueryParams,
  IPersonGeofenceItem,
  IPersonStatus,
  IPersonStatusResponse,
  IMapStatisticResponse,
  ITrackingItem,
} from '~/utils/interface/map';
import { API_DEFAULT_PARAMS } from '~/constants/map';

const TIMEZONE_SUFFIX_REGEX = /Z|[+-]\d{2}:?\d{2}$/i;
const TRACKTIME_BASE_REGEX = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}/;

/**
 * Normalize tracking timestamp for API query param `tracktime_utc`.
 *
 * Input: ISO-like datetime string, e.g. `2026-04-07T08:30:00.123Z` or `2026-04-07 15:30:00+0700`.
 * Output: database-friendly `YYYY-MM-DD HH:mm:ss` without milliseconds/timezone.
 *
 * Regex `Z|[+-]\d{2}:?\d{2}$` is used to detect timezone suffix at the end of the string:
 * - `Z` means UTC timezone.
 * - `+07:00`, `+0700`, `-05:00`, `-0500` are offset formats.
 */
const formatTrackTimeUtc = (value?: string | number | null): string | undefined => {
  const stringValue = typeof value === 'number' ? String(value) : value;

  if (typeof stringValue !== 'string' || !stringValue.trim()) return undefined;

  const rawValue = stringValue.trim();
  if (!TRACKTIME_BASE_REGEX.test(rawValue)) {
    console.warn('[mapApi] Unrecognized tracktime format, fallback normalization will be applied:', rawValue);
  }

  const normalized = rawValue.replace('T', ' ');
  const withoutTimezone = normalized.replace(TIMEZONE_SUFFIX_REGEX, '');
  const withoutMilliseconds = withoutTimezone.replace(/\.\d+$/, '');

  return withoutMilliseconds.length >= 19 ? withoutMilliseconds.slice(0, 19) : withoutMilliseconds;
};

const mapApi = {
  getMapTracking(username: string, tracktimeUtc?: string | null) {
    const formattedTracktimeUtc = formatTrackTimeUtc(tracktimeUtc);

    return axiosClient.get<ITrackingItem[]>('/api/map/tracking', {
      params: {
        username,
        ...(formattedTracktimeUtc !== undefined ? { tracktime_utc: formattedTracktimeUtc } : {}),
      },
    });
  },

  getPersons(params: IMapPersonQueryParams) {
    return axiosClient.get<IMapListResponse<IPersonGeofenceItem>>('/api/persons', {
      params: {
        search: '',
        startDate: '',
        endDate: '',
        simno: '',
        getall: true,
        pageIndex: API_DEFAULT_PARAMS.pageIndex,
        pageSize: API_DEFAULT_PARAMS.pageSize,
        ...params,
      },
    });
  },

  getLocations(params: IMapLocationQueryParams) {
    return axiosClient.get<IMapListResponse<ILocationGeofenceItem>>('/api/locations', {
      params: {
        search: '',
        startDate: '',
        endDate: '',
        pageIndex: API_DEFAULT_PARAMS.pageIndex,
        pageSize: API_DEFAULT_PARAMS.pageSize,
        ...params,
      },
    });
  },

  getPersonStatus() {
    return axiosClient.get<IPersonStatusResponse>('/api/data/person-status');
  },

  getLocationsByPerson(personId: number) {
    return axiosClient.get<IMapListResponse<ILocationGeofenceItem>>(`/api/locations/by-person/${personId}`);
  },

  getLocationsByUser(username: string) {
    return axiosClient.get<IMapListResponse<ILocationGeofenceItem>>(`/api/locations/by-user/${username}`);
  },

  getMapStatistic(username: string) {
    return axiosClient.get<IMapStatisticResponse>('/api/map/statistic', {
      params: { username },
    });
  },
};

export default mapApi;
